import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const FEATURE_META = {
  faq_reply:                { label: 'FAQ Antwoord', icon: '💬', desc: 'AI-antwoorden op klantvragen' },
  proposal:                 { label: 'Reisvoorstel', icon: '✈️', desc: 'Reisvoorstellen genereren' },
  newsletter:               { label: 'Nieuwsbrief', icon: '✉️', desc: 'Nieuwsbrieven genereren' },
  appointment_confirmation: { label: 'Bevestigingsmail', icon: '📅', desc: 'Afspraakbevestigingen' },
  appointment_prep:         { label: 'Voorbereiding Brief', icon: '📋', desc: 'Voorbereidingsnota afspraken' },
  appointment_reminder:     { label: 'Herinneringsmail', icon: '⏰', desc: 'Herinneringsmails afspraken' },
};

export default function AIInstructions() {
  const { authFetch } = useAuth();
  const [features, setFeatures] = useState([]);
  const [instructions, setInstructions] = useState({});
  const [activeFeature, setActiveFeature] = useState('faq_reply');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const docInputRef = useRef(null);

  useEffect(() => {
    loadInstructions();
    loadDocs();
  }, []);

  const loadInstructions = async () => {
    const res = await authFetch('/api/ai-instructions');
    const data = await res.json();
    setFeatures(data.features || []);
    setInstructions(data.instructions || {});
  };

  const loadDocs = async () => {
    const res = await authFetch('/api/documents');
    const data = await res.json();
    setDocs(Array.isArray(data) ? data : []);
  };

  const current = instructions[activeFeature] || {};

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    await authFetch(`/api/ai-instructions/${activeFeature}`, {
      method: 'POST',
      body: JSON.stringify({
        custom_system_prompt: current.custom_system_prompt || '',
        example_data: current.example_data || '',
        is_active: current.is_active !== false,
      }),
    });
    setSaveMsg('Opgeslagen!');
    setSaving(false);
    setTimeout(() => setSaveMsg(''), 3000);
    loadInstructions();
  };

  const handleReset = async () => {
    if (!window.confirm('Weet u zeker dat u de AI-instructies voor dit onderdeel wilt resetten naar de standaard?')) return;
    await authFetch(`/api/ai-instructions/${activeFeature}`, { method: 'DELETE' });
    loadInstructions();
  };

  const updateCurrent = (field, value) => {
    setInstructions(prev => ({
      ...prev,
      [activeFeature]: { ...prev[activeFeature], [field]: value }
    }));
  };

  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadMsg('');
    const fd = new FormData();
    files.forEach(f => fd.append('documents', f));
    try {
      const token = localStorage.getItem('tripdash_token');
      const res = await fetch('/api/documents/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (data.success) {
        setUploadMsg(`${data.uploaded.length} document(en) geüpload!`);
        loadDocs();
      }
    } catch (err) {
      setUploadMsg('Upload mislukt');
    }
    setUploading(false);
    setTimeout(() => setUploadMsg(''), 5000);
    e.target.value = '';
  };

  const handleDeleteDoc = async (id) => {
    await authFetch(`/api/documents/${id}`, { method: 'DELETE' });
    loadDocs();
  };

  const fmt = (bytes) => bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">AI Instructies</h1>
        <p className="text-gray-500 text-sm mt-1">Pas de AI-instructies aan voor elk onderdeel en upload trainingsmateriaal</p>
      </div>

      {/* Training documents section */}
      <div className="card mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-semibold text-gray-900">Trainingsmateriaal</h2>
            <p className="text-gray-500 text-xs mt-1">Upload uw eigen voorstellen, FAQ-antwoorden en brieven. De AI leert van uw schrijfstijl.</p>
          </div>
          <div>
            <button onClick={() => docInputRef.current?.click()} disabled={uploading}
              className="btn-primary text-sm">
              {uploading ? <span className="spinner" /> : '📤'}
              {uploading ? 'Uploaden...' : 'Document uploaden'}
            </button>
            <input ref={docInputRef} type="file" multiple accept=".pdf,.docx,.txt,.md" className="hidden" onChange={handleDocUpload} />
          </div>
        </div>

        {uploadMsg && (
          <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{uploadMsg}</div>
        )}

        {docs.length === 0 ? (
          <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-gray-500 text-sm">Nog geen documenten geüpload</p>
            <p className="text-gray-400 text-xs mt-1">Ondersteunde formaten: PDF, DOCX, TXT, Markdown</p>
          </div>
        ) : (
          <div className="space-y-2">
            {docs.map(doc => (
              <div key={doc.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-sm">
                    {doc.file_type?.includes('pdf') ? '📄' : doc.file_type?.includes('word') ? '📝' : '📃'}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{doc.filename}</p>
                    <p className="text-xs text-gray-400">{fmt(doc.file_size || 0)} · {new Date(doc.created_at).toLocaleDateString('nl-NL')}</p>
                  </div>
                </div>
                <button onClick={() => handleDeleteDoc(doc.id)}
                  className="text-red-400 hover:text-red-600 text-sm px-2 py-1 rounded hover:bg-red-50 transition-colors">
                  Verwijderen
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Feature instruction editor */}
      <div className="card">
        <h2 className="font-semibold text-gray-900 mb-4">AI-instructies per onderdeel</h2>

        {/* Feature tabs */}
        <div className="flex gap-2 flex-wrap mb-6 pb-4 border-b border-gray-100">
          {features.map(f => {
            const meta = FEATURE_META[f.key] || {};
            const hasCustom = instructions[f.key]?.custom_system_prompt;
            return (
              <button key={f.key} onClick={() => setActiveFeature(f.key)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                  activeFeature === f.key
                    ? 'text-white border-transparent shadow-sm'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600'
                }`}
                style={activeFeature === f.key ? { backgroundColor: 'var(--brand)', borderColor: 'var(--brand)' } : {}}>
                <span>{meta.icon}</span>
                {meta.label || f.label}
                {hasCustom && <span className="w-1.5 h-1.5 rounded-full bg-green-400 ml-1" title="Aangepaste instructie actief" />}
              </button>
            );
          })}
        </div>

        {activeFeature && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">
                  {FEATURE_META[activeFeature]?.icon} {FEATURE_META[activeFeature]?.label}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">{FEATURE_META[activeFeature]?.desc}</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-gray-600">Actief</span>
                <div className="relative">
                  <input type="checkbox" className="sr-only peer"
                    checked={current.is_active !== false}
                    onChange={e => updateCurrent('is_active', e.target.checked)} />
                  <div className="w-10 h-5 bg-gray-200 peer-checked:bg-blue-500 rounded-full transition-colors peer-focus:ring-2 peer-focus:ring-blue-300" />
                  <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
                </div>
              </label>
            </div>

            <div>
              <label className="label">Aangepaste systeem-instructie</label>
              <p className="text-xs text-gray-400 mb-2">Schrijf hier extra instructies voor de AI. Dit wordt vóór de standaard-instructie geplaatst. Laat leeg om de standaard te gebruiken.</p>
              <textarea
                className="input font-mono text-xs min-h-40 resize-y"
                placeholder={`Bijv.: Schrijf altijd in een warme, persoonlijke stijl. Gebruik altijd "u" als aanspreekwijze. Noem altijd onze USP: 25 jaar ervaring in Caribische reizen...`}
                value={current.custom_system_prompt || ''}
                onChange={e => updateCurrent('custom_system_prompt', e.target.value)}
              />
            </div>

            <div>
              <label className="label">Voorbeelddata / Schrijfstijl voorbeelden</label>
              <p className="text-xs text-gray-400 mb-2">Plak hier voorbeelden van uw eigen teksten. De AI gebruikt deze als stijlreferentie.</p>
              <textarea
                className="input text-xs min-h-48 resize-y"
                placeholder={`Plak hier voorbeelden van uw eigen voorstellen, brieven of antwoorden...\n\nBijv.:\nGeachte familie Janssen,\n\nMet veel plezier presenteer ik u ons exclusieve voorstel voor een onvergetelijke vakantie op Mallorca...`}
                value={current.example_data || ''}
                onChange={e => updateCurrent('example_data', e.target.value)}
              />
            </div>

            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
              <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
                {saving ? <span className="spinner" /> : null}
                {saving ? 'Opslaan...' : '💾 Instructies opslaan'}
              </button>
              <button onClick={handleReset} className="btn-secondary text-sm text-red-600 hover:bg-red-50">
                ↩ Reset naar standaard
              </button>
              {saveMsg && <span className="text-green-600 text-sm font-medium">{saveMsg}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
